import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { buildFetchParams } from '../lib/tsq-helpers';
import { FetchFnArgs } from '../types/apiTypes';
import { useFetch } from './useFetch';
import { useOperator } from '../contexts/OperatorContext';
import { useQueryFnDeps } from './useQueryFnDeps';
import { useUser } from '../contexts/UserContext';
import { BASE_URL_MAP } from '../lib/environment';

export interface CitrusViewInformation {
  currentUser: string;
  url: string | null;
  startTime: string;
}

interface ApiCitrusViewInformation {
  CurrentUser: string;
  Url: string;
  StartTime: string;
}

async function getCitrusViewStatus(args: FetchFnArgs<{ operatorId: number }>) {
  const params = buildFetchParams(args);
  const response = await fetch(
    `${params.BASE_URL}/${args.operatorId}/CitrusView`,
    params.options
  );

  if (response.ok) {
    const data = await response.json();
    return mapInfo(data);
  }
}

export const useCitrusViewInformation = () => {
  const [rebooting, setRebooting] = useState(false);
  const { error, loading, makeRequest } = useFetch();
  const operator = useOperator();
  const { email } = useUser();

  const deps = useQueryFnDeps();

  const info = useQuery({
    queryKey: [`citrusViewInfo-${operator.id}`],
    queryFn: ({ signal }) =>
      getCitrusViewStatus({ signal, operatorId: operator.id, ...deps }),
    refetchInterval: 1000,
  });

  useEffect(() => {
    if (info.data && info.data.currentUser === email) disconnect();
  }, [operator.isChanging]);

  useEffect(() => {
    if (!loading && !error && rebooting) {
      setTimeout(() => {
        setRebooting(false);
      }, 35000);
    }
  }, [rebooting, error, loading]);

  const connect = () => {
    makeRequest(`${BASE_URL_MAP[operator.type]}/${operator.id}/CitrusView`, 'post');
  };

  const disconnect = () => {
    makeRequest(`${BASE_URL_MAP[operator.type]}/${operator.id}/CitrusView`, 'delete');
  };

  const reboot = () => {
    setRebooting(true);
    makeRequest(`${BASE_URL_MAP[operator.type]}/${operator.id}/CitrusView/Reboot`, 'post');
  };

  return {
    connect,
    disconnect,
    info: info.data,
    loading,
    rebooting,
    reboot,
  };
};

const mapInfo = (info: ApiCitrusViewInformation): CitrusViewInformation => {
  return {
    currentUser: info?.CurrentUser ?? '',
    url: info?.Url ?? null,
    startTime: info?.StartTime ?? '',
  };
};
